import React, { useState, useRef, useEffect } from 'react';
import { FiX, FiUpload } from 'react-icons/fi';
import { userRequest } from '../../utils/requestMethods';

const PhotoUploadModal = ({
    isOpen,
    onClose,
    onUploadSuccess,
    uploadEndpoint,
    uploadData = {},
    title = 'Upload Photo',
    maxFileSize = 1024 * 1024, // 1MB default
    accept = 'image/*',
    // Crop/upload shape — defaults reproduce the original square profile-photo
    // behavior exactly. Pass e.g. aspectRatio={3} for a wide signature crop.
    aspectRatio = 1,
    canvasWidth = 500,
    fieldName = 'photo',
    method = 'post',
    mediaLabel = 'Photo',
    previewShape = 'circle', // 'circle' | 'rect'
    // 'png' keeps transparency (e.g. logos); 'jpeg' is smaller and gets a white background.
    outputFormat = 'jpeg',
}) => {
    // Photo upload states
    const [studentPhoto, setStudentPhoto] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [uploadSuccess, setUploadSuccess] = useState('');
    const [showCropModal, setShowCropModal] = useState(false);
    const [imageToCrop, setImageToCrop] = useState(null);
    const [cropArea, setCropArea] = useState({ x: 0, y: 0, width: 0, height: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [isResizing, setIsResizing] = useState(false);
    const fileInputRef = useRef(null);
    const cropContainerRef = useRef(null);
    const imageRef = useRef(null);

    // Reset state when modal closes
    useEffect(() => {
        if (!isOpen) {
            setStudentPhoto(null);
            setPhotoPreview(null);
            setImageToCrop(null);
            setShowCropModal(false);
            setUploadError('');
            setUploadSuccess('');
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    }, [isOpen]);

    // Photo upload functions
    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            setUploadError('Please select a valid image file (JPEG, PNG, etc.)');
            return;
        }

        setUploadError('');
        setUploadSuccess('');

        // Create preview and show crop modal
        const reader = new FileReader();
        reader.onload = (e) => {
            setImageToCrop(e.target.result);
            setShowCropModal(true);
        };
        reader.readAsDataURL(file);
    };

    // Initialize crop area when image loads
    const initializeCropArea = () => {
        if (imageRef.current && cropContainerRef.current) {
            const img = imageRef.current;
            const container = cropContainerRef.current;
            const containerWidth = container.offsetWidth;
            const containerHeight = container.offsetHeight;

            // Calculate image display bounds (for object-contain)
            const imgAspect = img.naturalWidth / img.naturalHeight;
            const containerAspect = containerWidth / containerHeight;

            let imgDisplayWidth, imgDisplayHeight, imgDisplayX, imgDisplayY;

            if (imgAspect > containerAspect) {
                imgDisplayWidth = containerWidth;
                imgDisplayHeight = containerWidth / imgAspect;
                imgDisplayX = 0;
                imgDisplayY = (containerHeight - imgDisplayHeight) / 2;
            } else {
                imgDisplayHeight = containerHeight;
                imgDisplayWidth = containerHeight * imgAspect;
                imgDisplayX = (containerWidth - imgDisplayWidth) / 2;
                imgDisplayY = 0;
            }

            // Calculate initial crop area (fixed aspectRatio, centered on image,
            // as large as fits within 80% of the image's display bounds).
            const maxWidth = imgDisplayWidth * 0.8;
            const maxHeight = imgDisplayHeight * 0.8;
            let width = maxWidth;
            let height = width / aspectRatio;
            if (height > maxHeight) {
                height = maxHeight;
                width = height * aspectRatio;
            }
            const x = imgDisplayX + (imgDisplayWidth - width) / 2;
            const y = imgDisplayY + (imgDisplayHeight - height) / 2;

            setCropArea({ x, y, width, height });
        }
    };

    // Helper function to get coordinates from event (works for both mouse and touch)
    const getEventCoordinates = (e) => {
        if (e.touches && e.touches.length > 0) {
            return { x: e.touches[0].clientX, y: e.touches[0].clientY };
        }
        return { x: e.clientX, y: e.clientY };
    };

    // Handle crop area drag (works for both mouse and touch)
    const handleCropStart = (e) => {
        e.preventDefault();
        if (!cropContainerRef.current) return;

        const container = cropContainerRef.current;
        const rect = container.getBoundingClientRect();
        const coords = getEventCoordinates(e);

        setIsDragging(true);
        setDragStart({
            x: coords.x - rect.left - cropArea.x,
            y: coords.y - rect.top - cropArea.y
        });
    };

    const handleCropMove = (e) => {
        if (!isDragging && !isResizing) return;
        if (!cropContainerRef.current || !imageRef.current) return;

        e.preventDefault(); // Prevent scrolling on mobile
        const coords = getEventCoordinates(e);
        const container = cropContainerRef.current;
        const img = imageRef.current;
        const rect = container.getBoundingClientRect();

        // Calculate image display bounds (for object-contain)
        const containerWidth = rect.width;
        const containerHeight = rect.height;
        const imgAspect = img.naturalWidth / img.naturalHeight;
        const containerAspect = containerWidth / containerHeight;

        let imgDisplayWidth, imgDisplayHeight, imgDisplayX, imgDisplayY;

        if (imgAspect > containerAspect) {
            imgDisplayWidth = containerWidth;
            imgDisplayHeight = containerWidth / imgAspect;
            imgDisplayX = 0;
            imgDisplayY = (containerHeight - imgDisplayHeight) / 2;
        } else {
            imgDisplayHeight = containerHeight;
            imgDisplayWidth = containerHeight * imgAspect;
            imgDisplayX = (containerWidth - imgDisplayWidth) / 2;
            imgDisplayY = 0;
        }

        if (isDragging) {
            let newX = coords.x - rect.left - dragStart.x;
            let newY = coords.y - rect.top - dragStart.y;

            // Constrain to image display bounds
            newX = Math.max(imgDisplayX, Math.min(newX, imgDisplayX + imgDisplayWidth - cropArea.width));
            newY = Math.max(imgDisplayY, Math.min(newY, imgDisplayY + imgDisplayHeight - cropArea.height));

            setCropArea(prev => ({ ...prev, x: newX, y: newY }));
        } else if (isResizing) {
            const maxWidth = imgDisplayX + imgDisplayWidth - cropArea.x;
            const maxHeight = imgDisplayY + imgDisplayHeight - cropArea.y;
            let newWidth = Math.max(100, Math.min(coords.x - rect.left - cropArea.x, maxWidth));
            let newHeight = newWidth / aspectRatio;
            if (newHeight > maxHeight) {
                newHeight = maxHeight;
                newWidth = newHeight * aspectRatio;
            }

            setCropArea(prev => ({
                ...prev,
                width: newWidth,
                height: newHeight
            }));
        }
    };

    const handleCropEnd = () => {
        setIsDragging(false);
        setIsResizing(false);
    };

    // Handle resize (works for both mouse and touch)
    const handleResizeStart = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsResizing(true);
        const coords = getEventCoordinates(e);
        setDragStart({
            x: coords.x,
            y: coords.y
        });
    };

    // Crop and compress image
    const applyCropAndCompress = async () => {
        if (!imageToCrop || !imageRef.current || !cropContainerRef.current) return;

        const img = imageRef.current;
        const container = cropContainerRef.current;
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Calculate crop coordinates relative to image
        // Get image display dimensions (object-contain maintains aspect ratio)
        const containerWidth = container.offsetWidth;
        const containerHeight = container.offsetHeight;
        const imgAspect = img.naturalWidth / img.naturalHeight;
        const containerAspect = containerWidth / containerHeight;

        let imgDisplayWidth, imgDisplayHeight, imgDisplayX, imgDisplayY;

        if (imgAspect > containerAspect) {
            // Image is wider - fit to width
            imgDisplayWidth = containerWidth;
            imgDisplayHeight = containerWidth / imgAspect;
            imgDisplayX = 0;
            imgDisplayY = (containerHeight - imgDisplayHeight) / 2;
        } else {
            // Image is taller - fit to height
            imgDisplayHeight = containerHeight;
            imgDisplayWidth = containerHeight * imgAspect;
            imgDisplayX = (containerWidth - imgDisplayWidth) / 2;
            imgDisplayY = 0;
        }

        // Calculate scale factors
        const scaleX = img.naturalWidth / imgDisplayWidth;
        const scaleY = img.naturalHeight / imgDisplayHeight;

        // Convert crop area coordinates to image coordinates
        const cropX = (cropArea.x - imgDisplayX) * scaleX;
        const cropY = (cropArea.y - imgDisplayY) * scaleY;
        const cropWidth = cropArea.width * scaleX;
        const cropHeight = cropArea.height * scaleY;

        // Ensure crop coordinates are within image bounds
        const finalCropX = Math.max(0, Math.min(cropX, img.naturalWidth));
        const finalCropY = Math.max(0, Math.min(cropY, img.naturalHeight));
        const finalCropWidth = Math.min(cropWidth, img.naturalWidth - finalCropX);
        const finalCropHeight = Math.min(cropHeight, img.naturalHeight - finalCropY);

        const isPng = outputFormat === 'png';
        const mimeType = isPng ? 'image/png' : 'image/jpeg';

        // Draws the crop at the given size. JPEG has no transparency, so transparent areas
        // (e.g. a PNG signature) are filled white instead of turning black.
        const drawCrop = (width, height) => {
            canvas.width = width;
            canvas.height = height;
            if (!isPng) {
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(0, 0, width, height);
            }
            ctx.drawImage(
                img,
                finalCropX, finalCropY, finalCropWidth, finalCropHeight,
                0, 0, width, height
            );
        };

        // Set canvas size to the target aspect ratio (500x500 for a square
        // profile photo by default; wider for e.g. a signature crop)
        const targetWidth = canvasWidth;
        const targetHeight = Math.round(canvasWidth / aspectRatio);
        drawCrop(targetWidth, targetHeight);

        // Compress image to under maxFileSize
        let quality = 0.9;
        let blob = await new Promise(resolve => canvas.toBlob(resolve, mimeType, quality));

        // Reduce quality until under maxFileSize (PNG ignores quality, so skip straight to resizing)
        while (!isPng && blob.size > maxFileSize && quality > 0.1) {
            quality -= 0.1;
            blob = await new Promise(resolve => canvas.toBlob(resolve, mimeType, quality));
        }

        // If still too large, reduce dimensions
        if (blob.size > maxFileSize) {
            let width = targetWidth;
            while (blob.size > maxFileSize && width > 200) {
                width -= 50;
                drawCrop(width, Math.round(width / aspectRatio));
                blob = await new Promise(resolve => canvas.toBlob(resolve, mimeType, 0.8));
            }
        }

        // Create file from blob
        const file = new File([blob], `${fieldName}.${isPng ? 'png' : 'jpg'}`, { type: mimeType });
        setStudentPhoto(file);
        setPhotoPreview(URL.createObjectURL(blob));
        setShowCropModal(false);
        setImageToCrop(null);
    };

    const handleUploadPhoto = async () => {
        if (!studentPhoto || !uploadEndpoint) {
            setUploadError(`Please select and crop a ${mediaLabel.toLowerCase()} first`);
            return;
        }

        // Validate file size
        if (studentPhoto.size > maxFileSize) {
            setUploadError(`File size exceeds ${(maxFileSize / (1024 * 1024)).toFixed(1)}MB. Please try again.`);
            return;
        }

        setIsUploading(true);
        setUploadError('');
        setUploadSuccess('');

        try {
            const formData = new FormData();
            formData.append(fieldName, studentPhoto);

            // Append additional data
            Object.keys(uploadData).forEach(key => {
                formData.append(key, uploadData[key]);
            });

            const response = await userRequest[method](
                uploadEndpoint,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            if (response.status >= 200 && response.status < 300) {
                setUploadSuccess(`${mediaLabel} uploaded successfully!`);
                setUploadError('');

                // Call success callback
                if (onUploadSuccess) {
                    onUploadSuccess(response.data);
                }

                // Close modal after successful upload
                setTimeout(() => {
                    setUploadSuccess('');
                    onClose();
                    setStudentPhoto(null);
                    setPhotoPreview(null);
                }, 2000);
            } else {
                throw new Error(response.data?.message || 'Upload failed');
            }
        } catch (error) {
            console.error('Photo upload error:', error);
            const errorMessage = error.response?.data?.message ||
                error.message ||
                `Failed to upload ${mediaLabel.toLowerCase()}. Please try again.`;
            setUploadError(errorMessage);

            // Hide error message after 5 seconds
            setTimeout(() => {
                setUploadError('');
            }, 5000);
        } finally {
            setIsUploading(false);
        }
    };

    const handleRemovePhoto = () => {
        setStudentPhoto(null);
        setPhotoPreview(null);
        setImageToCrop(null);
        setUploadError('');
        setUploadSuccess('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleCancelCrop = () => {
        setShowCropModal(false);
        setImageToCrop(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    if (!isOpen) return null;

    return (
        <>
            {/* Photo Upload Modal */}
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-md flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-xl font-semibold text-gray-800">{title}</h2>
                        <button
                            onClick={onClose}
                            className="text-gray-500 hover:text-gray-700 p-2"
                            disabled={isUploading}
                        >
                            <FiX className="w-5 h-5" />
                        </button>
                    </div>

                    <div className="space-y-4">
                        {/* File Input */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Select {mediaLabel}
                            </label>
                            <div className="relative">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept={accept}
                                    onChange={handleFileSelect}
                                    className="hidden"
                                />
                                <button
                                    type="button"
                                    onClick={() => fileInputRef.current?.click()}
                                    className="w-full flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-[#3F3DA5] hover:bg-gray-50 transition-colors"
                                >
                                    <FiUpload className="w-5 h-5 text-gray-400 mr-2" />
                                    <span className="text-gray-600">Choose file or drag and drop</span>
                                </button>
                            </div>
                        </div>

                        {/* Photo Preview */}
                        {photoPreview && (
                            <div className="text-center">
                                <p className="text-sm font-medium text-gray-700 mb-2">Preview</p>
                                <div className="relative inline-block">
                                    <img
                                        src={photoPreview}
                                        alt="Preview"
                                        className={
                                            previewShape === 'rect'
                                                ? 'w-56 h-auto rounded-lg object-contain border-4 border-gray-200 bg-gray-50'
                                                : 'w-32 h-32 rounded-full object-cover border-4 border-gray-200'
                                        }
                                        style={previewShape === 'rect' ? { aspectRatio } : undefined}
                                    />
                                    <button
                                        onClick={handleRemovePhoto}
                                        className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1"
                                        title={`Remove ${mediaLabel.toLowerCase()}`}
                                    >
                                        <FiX className="w-3 h-3" />
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Upload Progress */}
                        {isUploading && (
                            <div className="text-center">
                                <div className="inline-block w-6 h-6 border-2 border-[#3F3DA5] border-t-transparent rounded-full animate-spin"></div>
                                <p className="text-sm text-gray-600 mt-2">Uploading {mediaLabel.toLowerCase()}...</p>
                            </div>
                        )}

                        {/* Success Message */}
                        {uploadSuccess && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                <p className="text-sm text-green-800 text-center">{uploadSuccess}</p>
                            </div>
                        )}

                        {/* Error Message */}
                        {uploadError && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                <p className="text-sm text-red-800 text-center">{uploadError}</p>
                            </div>
                        )}

                        {/* Action Button */}
                        <button
                            onClick={handleUploadPhoto}
                            disabled={!studentPhoto || isUploading}
                            className="w-full bg-[#3F3DA5] hover:bg-blue-800 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-colors"
                        >
                            {isUploading ? 'Uploading...' : `Upload ${mediaLabel}`}
                        </button>
                    </div>

                    {/* File Requirements */}
                    <div className="mt-4 text-xs text-gray-500">
                        <p>Supported formats: JPEG, PNG</p>
                        <p>Maximum file size: {(maxFileSize / (1024 * 1024)).toFixed(1)}MB (will be compressed automatically)</p>
                    </div>
                </div>
            </div>

            {/* Crop Modal */}
            {showCropModal && imageToCrop && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-md flex items-center justify-center z-[60] p-4"
                    onMouseMove={handleCropMove}
                    onMouseUp={handleCropEnd}
                    onMouseLeave={handleCropEnd}
                    onTouchMove={handleCropMove}
                    onTouchEnd={handleCropEnd}
                >
                    <div className="bg-white rounded-2xl w-full max-w-2xl p-6 shadow-xl">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-semibold text-gray-800">Crop {mediaLabel}</h2>
                            <button
                                onClick={handleCancelCrop}
                                className="text-gray-500 hover:text-gray-700 p-2"
                            >
                                <FiX className="w-5 h-5" />
                            </button>
                        </div>

                        <div
                            ref={cropContainerRef}
                            className="relative w-full h-96 bg-gray-100 rounded-lg overflow-hidden mb-4"
                            style={{
                                minHeight: '400px',
                                touchAction: 'none',
                                WebkitTouchCallout: 'none',
                                WebkitUserSelect: 'none',
                                userSelect: 'none'
                            }}
                        >
                            <img
                                ref={imageRef}
                                src={imageToCrop}
                                alt="Crop preview"
                                className="absolute inset-0 w-full h-full object-contain"
                                onLoad={initializeCropArea}
                            />

                            {/* Crop overlay */}
                            <div className="absolute inset-0">
                                {/* Dark overlay */}
                                <div
                                    className="absolute inset-0 bg-black opacity-50"
                                    style={{
                                        clipPath: `polygon(
                                            0% 0%, 
                                            0% 100%, 
                                            ${cropArea.x}px 100%, 
                                            ${cropArea.x}px ${cropArea.y}px, 
                                            ${cropArea.x + cropArea.width}px ${cropArea.y}px, 
                                            ${cropArea.x + cropArea.width}px ${cropArea.y + cropArea.height}px, 
                                            ${cropArea.x}px ${cropArea.y + cropArea.height}px, 
                                            ${cropArea.x}px 100%, 
                                            100% 100%, 
                                            100% 0%
                                        )`
                                    }}
                                />

                                {/* Crop area */}
                                <div
                                    className="absolute border-2 border-white cursor-move shadow-lg touch-none"
                                    style={{
                                        left: `${cropArea.x}px`,
                                        top: `${cropArea.y}px`,
                                        width: `${cropArea.width}px`,
                                        height: `${cropArea.height}px`,
                                        userSelect: 'none',
                                        WebkitUserSelect: 'none',
                                    }}
                                    onMouseDown={handleCropStart}
                                    onTouchStart={handleCropStart}
                                >
                                    {/* Resize handle */}
                                    <div
                                        className="absolute bottom-0 right-0 w-6 h-6 sm:w-4 sm:h-4 bg-blue-500 border-2 border-white rounded-full cursor-se-resize touch-none"
                                        style={{ transform: 'translate(50%, 50%)' }}
                                        onMouseDown={handleResizeStart}
                                        onTouchStart={handleResizeStart}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={handleCancelCrop}
                                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={applyCropAndCompress}
                                className="px-4 py-2 bg-[#3F3DA5] hover:bg-blue-800 text-white rounded-lg transition-colors"
                            >
                                Apply Crop
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default PhotoUploadModal;

